// routes/settings_.club-subscription.$clubId.tsx
// -----------------------------------------------------------------------------
// Per-club subscription management page.
// Shows subscription info and links to Stripe customer portal.
// -----------------------------------------------------------------------------

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/components/providers/AuthProvider";
import {
  useGetClubQuery,
  useFetchUserSubscribedClubsQuery,
} from "@/api/clubsApi";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/settings_/club-subscription/$clubId")({
  beforeLoad: requireAuth,
  component: ClubSubscriptionPage,
});

function ClubSubscriptionPage() {
  const { clubId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const {
    data: club,
    isLoading: isLoadingClub,
    error: clubError,
  } = useGetClubQuery(clubId);

  const { data: subscribedClubs } = useFetchUserSubscribedClubsQuery(
    user?.id || "",
    { skip: !user?.id }
  );

  // Find this club's subscription
  const subscribedClub = subscribedClubs?.find((c) => c.id === clubId);
  const subscription = subscribedClub?.subscription;

  useHeaderConfig(
    {
      breadcrumbs: [
        { label: "Settings", path: "/settings" },
        { label: club?.name || "Subscription" },
      ],
    },
    [club?.name]
  );

  const openCustomerPortal = async () => {
    setIsLoadingPortal(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      const returnUrl = `${window.location.origin}/settings/club-subscription/${clubId}`;

      const body: Record<string, string> = { returnUrl };
      if (subscription?.stripe_subscription_id) {
        body.subscriptionId = subscription.stripe_subscription_id;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-customer-portal`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();
      if (data?.portalUrl) {
        window.open(data.portalUrl, "_blank");
      }
    } catch (error) {
      console.error("Failed to open customer portal:", error);
    } finally {
      setIsLoadingPortal(false);
    }
  };

  // Loading state
  if (isLoadingClub) {
    return (
      <AppLayout>
        <PageTransition>
          <div className="p-6 pb-24">
            <div className="max-w-lg mx-auto space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-20 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
          </div>
        </PageTransition>
      </AppLayout>
    );
  }

  // Error state
  if (clubError || !club) {
    return (
      <AppLayout>
        <PageTransition>
          <div className="p-6 pb-24">
            <div className="max-w-lg mx-auto text-center space-y-4">
              <p className="text-text-secondary">
                {clubError
                  ? "Failed to load club information."
                  : "Club not found."}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate({ to: "/settings" })}
              >
                Go Back
              </Button>
            </div>
          </div>
        </PageTransition>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-6 pb-24">
          <div className="max-w-lg mx-auto space-y-6">
            {/* Description */}
            <p className="text-body-2 text-text-secondary">
              Subscription and payment information for your club membership.
              This pricing is set by the club host.
            </p>

            {/* Action rows */}
            <div className="bg-surface-1 rounded-2xl overflow-hidden">
              {/* Manage subscription */}
              <button
                className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                onClick={() => openCustomerPortal()}
                disabled={isLoadingPortal}
              >
                <div className="w-6 flex-shrink-0 flex items-center justify-center">
                  <Icon
                    name="gear"
                    size={20}
                    className="text-text-primary"
                  />
                </div>
                <span className="text-body-1 text-text-primary flex-1">
                  Manage subscription
                </span>
                <Icon
                  name="arrow-up-right-from-square"
                  size={16}
                  className="text-text-secondary"
                />
              </button>

              <div className="h-px bg-border-subtle ml-12" />

              {/* Subscription history */}
              <button
                className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                onClick={() => openCustomerPortal()}
                disabled={isLoadingPortal}
              >
                <div className="w-6 flex-shrink-0 flex items-center justify-center">
                  <Icon
                    name="rectangle-history"
                    size={20}
                    className="text-text-primary"
                  />
                </div>
                <span className="text-body-1 text-text-primary flex-1">
                  Subscription history
                </span>
                <Icon
                  name="arrow-up-right-from-square"
                  size={16}
                  className="text-text-secondary"
                />
              </button>
            </div>
          </div>
        </div>
      </PageTransition>
    </AppLayout>
  );
}
